/**
 * Request Throttler Utility
 * Prevents API rate limiting by queuing and throttling requests
 */

class RequestThrottler {
  constructor(options = {}) {
    this.delay = options.delay || 200 // Delay between requests in ms
    this.maxConcurrent = options.maxConcurrent || 3 // Max concurrent requests
    this.queue = []
    this.activeRequests = 0
  }

  /**
   * Add a request to the queue
   * @param {Function} requestFn - Async function that returns a promise
   * @returns {Promise} Promise that resolves with the request result
   */
  async throttle(requestFn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ requestFn, resolve, reject })
      this.processQueue()
    })
  }

  /**
   * Process the request queue
   */
  async processQueue() {
    // If we've hit max concurrent requests or queue is empty, do nothing
    if (this.activeRequests >= this.maxConcurrent || this.queue.length === 0) {
      return
    }

    // Get next request from queue
    const { requestFn, resolve, reject } = this.queue.shift()
    this.activeRequests++

    try {
      // Execute the request
      const result = await requestFn()
      resolve(result)
    } catch (error) {
      reject(error)
    } finally {
      this.activeRequests--

      // Wait before processing next request
      if (this.queue.length > 0) {
        setTimeout(() => this.processQueue(), this.delay)
      }
    }
  }

  /**
   * Throttle multiple requests sequentially with delays
   * @param {Array<Function>} requests - Array of async functions
   * @returns {Promise<Array>} Array of results
   */
  async throttleAll(requests) {
    const results = []

    for (const requestFn of requests) {
      try {
        const result = await this.throttle(requestFn)
        results.push(result)
      } catch (error) {
        console.error('Throttled request failed:', error)
        results.push(null)
      }
    }

    return results
  }

  /**
   * Clear the queue
   */
  clear() {
    this.queue = []
    this.activeRequests = 0
  }
}

// Create a singleton instance for Autodesk API calls
export const autodeskApiThrottler = new RequestThrottler({
  delay: 300, // 300ms delay between requests
  maxConcurrent: 2, // Only 2 concurrent requests to Autodesk API
})

export default RequestThrottler
